import React, { useState, useEffect, useRef } from "react";
import { Play, RotateCcw, Settings, Target, Home } from "lucide-react";

const StationLocationExplorer = () => {
  // 家庭配置（只包含猴子数量，位置自动计算）
  const [families, setFamilies] = useState([
    { id: 1, monkeys: 2 },
    { id: 2, monkeys: 5 },
    { id: 3, monkeys: 3 },
  ]);

  // 基站位置（0-1之间的比例）
  const [stationPosition, setStationPosition] = useState(0.5);

  // 计算家庭在地图上的实际位置
  const calculateFamilyPositions = () => {
    const familyCount = families.length;
    if (familyCount === 1) {
      return [{ ...families[0], position: 0.5 }];
    }

    return families.map((family, index) => ({
      ...family,
      position: index / (familyCount - 1), // 0到1之间均匀分布
    }));
  };

  // 计算地图宽度（基于家庭数量，但有最大限制）
  const calculateMapWidth = () => {
    const familyCount = families.length;
    const baseWidth = 400;
    const maxWidth = 700;
    const minWidth = 300;

    if (familyCount <= 2) return minWidth;
    if (familyCount >= 6) return maxWidth;

    return baseWidth + (familyCount - 2) * 75;
  };

  // 游戏状态
  const [gameState, setGameState] = useState({
    isRunning: false,
    isComplete: false,
    animationProgress: 0,
  });

  // 历史记录
  const [history, setHistory] = useState([]);

  // 拖动状态
  const [isDragging, setIsDragging] = useState(false);
  const svgRef = useRef(null);

  // 显示选项
  const [displayOptions, setDisplayOptions] = useState({
    showIndividualMonkeys: true,
    showDistanceLabels: true,
    showOptimalHint: false, // 默认关闭最优提示
    showFamilyContribution: true,
  });

  // 计算总距离和各家庭贡献
  const calculateDistances = () => {
    const familyPositions = calculateFamilyPositions();
    const familyCount = families.length;

    // 计算距离单位：相邻家庭间距离为1
    const distanceUnit = familyCount > 1 ? familyCount - 1 : 1;

    let totalDistance = 0;
    const contributions = familyPositions.map((family) => {
      // 将比例位置转换为以相邻家庭间距离为单位1的距离
      const familyPos = family.position * distanceUnit;
      const stationPos = stationPosition * distanceUnit;
      const distance = Math.abs(familyPos - stationPos);
      const contribution = distance * family.monkeys;
      totalDistance += contribution;
      return {
        familyId: family.id,
        distance,
        contribution,
        monkeys: family.monkeys,
      };
    });

    return { totalDistance, contributions, distanceUnit };
  };

  // 计算理论最优位置
  const calculateOptimalPosition = () => {
    const familyPositions = calculateFamilyPositions();

    // 创建加权位置数组
    const weightedPositions = [];
    familyPositions.forEach((family) => {
      for (let i = 0; i < family.monkeys; i++) {
        weightedPositions.push(family.position);
      }
    });

    // 排序
    weightedPositions.sort((a, b) => a - b);

    // 找中位数
    const medianIndex = Math.floor(weightedPositions.length / 2);
    if (weightedPositions.length % 2 === 0) {
      return (
        (weightedPositions[medianIndex - 1] + weightedPositions[medianIndex]) /
        2
      );
    } else {
      return weightedPositions[medianIndex];
    }
  };

  // 开始动画
  const startAnimation = () => {
    if (gameState.isRunning) return;

    setGameState((prev) => ({
      ...prev,
      isRunning: true,
      isComplete: false,
      animationProgress: 0,
    }));

    // 动画过程
    let progress = 0;
    const animate = () => {
      progress += 1;
      setGameState((prev) => ({ ...prev, animationProgress: progress }));

      if (progress >= 100) {
        // 动画完成，记录本次尝试
        const { totalDistance, contributions } = calculateDistances();
        const newRecord = {
          id: Date.now(),
          stationPosition,
          totalDistance,
          contributions: [...contributions],
          timestamp: new Date(),
        };
        setHistory((prev) => [...prev, newRecord]);

        setGameState((prev) => ({
          ...prev,
          isRunning: false,
          isComplete: true,
        }));
      } else {
        setTimeout(animate, 30);
      }
    };

    setTimeout(animate, 100);
  };

  // 重置
  const reset = () => {
    setGameState({
      isRunning: false,
      isComplete: false,
      animationProgress: 0,
    });
    setStationPosition(0.5); // 重置到中间位置
  };

  // 处理基站拖动
  const handleStationDrag = (e) => {
    if (!isDragging || !svgRef.current) return;

    const rect = svgRef.current.getBoundingClientRect();
    const svgWidth = rect.width;
    const pathWidth = svgWidth - 100;
    const x = e.clientX - rect.left;
    const relativeX = Math.max(0, Math.min(pathWidth, x - 50));
    const newPosition = (relativeX / pathWidth) * 9 + 1;

    setStationPosition(
      Math.max(1, Math.min(10, Math.round(newPosition * 10) / 10))
    );
  };

  // 处理滑动条变化
  const handleSliderChange = (e) => {
    const newPosition = parseFloat(e.target.value);

    // 检查是否靠近家庭位置，实现吸附功能
    const familyPositions = calculateFamilyPositions();
    const snapThreshold = 0.05; // 5%的吸附范围

    let finalPosition = newPosition;
    for (const family of familyPositions) {
      if (Math.abs(newPosition - family.position) < snapThreshold) {
        finalPosition = family.position;
        break;
      }
    }

    setStationPosition(finalPosition);
  };

  // 添加/删除家庭
  const addFamily = () => {
    const newId = Math.max(...families.map((f) => f.id)) + 1;
    const newPosition = Math.max(...families.map((f) => f.position)) + 1;
    setFamilies((prev) => [
      ...prev,
      {
        id: newId,
        position: Math.min(newPosition, 10),
        monkeys: 2,
      },
    ]);
  };

  const removeFamily = (id) => {
    if (families.length > 1) {
      setFamilies((prev) => prev.filter((f) => f.id !== id));
    }
  };

  // 更新家庭配置 - 修复参数问题
  const updateFamily = (id, field, value) => {
    setFamilies((prev) =>
      prev.map((family) =>
        family.id === id ? { ...family, [field]: parseInt(value) || 1 } : family
      )
    );
  };

  // 渲染轨迹图
  const renderTrajectory = () => {
    if (!gameState.isComplete) return null;

    const { contributions } = calculateDistances();
    const familyPositions = calculateFamilyPositions();
    const mapWidth = calculateMapWidth();
    const svgWidth = mapWidth + 100;
    const distanceUnit = families.length > 1 ? families.length - 1 : 1;

    // 为每只猴子创建箭头
    const monkeyArrows = [];
    let arrowIndex = 0;

    // 按基站左右分组
    const leftFamilies = familyPositions.filter(
      (f) => f.position < stationPosition
    );
    const rightFamilies = familyPositions.filter(
      (f) => f.position > stationPosition
    );

    // 左侧猴子（从远到近）
    leftFamilies
      .sort((a, b) => a.position - b.position)
      .forEach((family) => {
        for (let i = 0; i < family.monkeys; i++) {
          const startX = 50 + family.position * mapWidth;
          const endX = 50 + stationPosition * mapWidth;
          const arrowY = 30 + arrowIndex * 20;

          monkeyArrows.push(
            <g key={`left-${family.id}-${i}`}>
              <line
                x1={startX}
                y1={arrowY}
                x2={endX}
                y2={arrowY}
                stroke="#10b981"
                strokeWidth="2"
                markerEnd="url(#arrowhead-right)"
              />
              {displayOptions.showDistanceLabels && i === 0 && (
                <text
                  x={(startX + endX) / 2}
                  y={arrowY - 5}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#10b981"
                  fontWeight="bold"
                >
                  距离
                  {formatNumber(
                    Math.abs((family.position - stationPosition) * distanceUnit)
                  )}
                </text>
              )}
            </g>
          );
          arrowIndex++;
        }
      });

    // 右侧猴子（从远到近）
    rightFamilies
      .sort((a, b) => b.position - a.position)
      .forEach((family) => {
        for (let i = 0; i < family.monkeys; i++) {
          const startX = 50 + family.position * mapWidth;
          const endX = 50 + stationPosition * mapWidth;
          const arrowY = 30 + arrowIndex * 20;

          monkeyArrows.push(
            <g key={`right-${family.id}-${i}`}>
              <line
                x1={startX}
                y1={arrowY}
                x2={endX}
                y2={arrowY}
                stroke="#f59e0b"
                strokeWidth="2"
                markerEnd="url(#arrowhead-left)"
              />
              {displayOptions.showDistanceLabels && i === 0 && (
                <text
                  x={(startX + endX) / 2}
                  y={arrowY - 5}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#f59e0b"
                  fontWeight="bold"
                >
                  距离
                  {formatNumber(
                    Math.abs((family.position - stationPosition) * distanceUnit)
                  )}
                </text>
              )}
            </g>
          );
          arrowIndex++;
        }
      });

    const trajectoryHeight = Math.max(100, arrowIndex * 20 + 50);

    return (
      <svg
        width="100%"
        height={trajectoryHeight}
        viewBox={`0 0 ${svgWidth} ${trajectoryHeight}`}
      >
        <defs>
          <marker
            id="arrowhead-right"
            markerWidth="6"
            markerHeight="5"
            refX="5"
            refY="2.5"
            orient="auto"
          >
            <polygon points="0 0, 6 2.5, 0 5" fill="#10b981" />
          </marker>
          <marker
            id="arrowhead-left"
            markerWidth="6"
            markerHeight="5"
            refX="5"
            refY="2.5"
            orient="auto"
          >
            <polygon points="6 2.5, 0 0, 0 5" fill="#f59e0b" />
          </marker>
        </defs>
        {monkeyArrows}
      </svg>
    );
  };

  const { totalDistance, contributions } = calculateDistances();
  const optimalPosition = calculateOptimalPosition();
  const isOptimal = Math.abs(stationPosition - optimalPosition) <= 0.05;

  // 格式化数字显示（整数时不显示小数点，处理浮点数精度问题）
  const formatNumber = (num) => {
    // 处理浮点数精度问题，保留足够精度后再判断是否为整数
    const rounded = Math.round(num * 100) / 100;
    return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(2);
  };

  // 滑动条样式
  const sliderStyle = `
    .slider::-webkit-slider-thumb {
      appearance: none;
      height: 20px;
      width: 20px;
      border-radius: 50%;
      background: #059669;
      border: 2px solid #ffffff;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      cursor: pointer;
    }
    .slider::-moz-range-thumb {
      height: 20px;
      width: 20px;
      border-radius: 50%;
      background: #059669;
      border: 2px solid #ffffff;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      cursor: pointer;
    }
  `;

  return (
    <div className="w-full max-w-6xl mx-auto p-6 bg-gradient-to-b from-blue-100 to-purple-100 rounded-lg shadow-lg">
      {/* 滑动条样式 */}
      <style>{sliderStyle}</style>

      <h1 className="text-3xl font-bold text-center mb-6 text-purple-800">
        🐵🏠 基站选址探索工具
      </h1>

      {/* 配置与控制面板 */}
      <div className="bg-white rounded-lg p-4 shadow-md mb-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
          <Settings size={16} className="mr-2" />
          配置与控制
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 左侧：家庭配置 */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">家庭配置</h4>
              <div className="flex space-x-2">
                <button
                  onClick={addFamily}
                  disabled={gameState.isRunning || families.length >= 8}
                  className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 disabled:opacity-50"
                >
                  + 添加家庭
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {families.map((family) => (
                <div
                  key={family.id}
                  className="flex items-center space-x-1 p-1.5 border rounded text-sm w-fit bg-gray-50"
                >
                  <span className="text-xs font-medium text-gray-600">
                    F{family.id}
                  </span>
                  <input
                    type="number"
                    value={family.monkeys}
                    onChange={(e) =>
                      updateFamily(family.id, "monkeys", e.target.value)
                    }
                    className="w-10 px-1 py-0.5 border rounded text-xs text-center"
                    min="1"
                    max="20"
                    disabled={gameState.isRunning}
                    title="猴子数量"
                  />
                  <span className="text-xs text-gray-500">只</span>
                  {families.length > 1 && (
                    <button
                      onClick={() => removeFamily(family.id)}
                      disabled={gameState.isRunning}
                      className="w-4 h-4 bg-red-500 text-white rounded text-xs hover:bg-red-600 disabled:opacity-50 flex items-center justify-center ml-1"
                      title="删除"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-2 text-xs text-gray-500">
              💡 快速设置：
              <button
                onClick={() => {
                  setFamilies([
                    { id: 1, monkeys: 1 },
                    { id: 2, monkeys: 2 },
                    { id: 3, monkeys: 3 },
                    { id: 4, monkeys: 4 },
                    { id: 5, monkeys: 5 },
                    { id: 6, monkeys: 6 },
                    { id: 7, monkeys: 7 },
                    { id: 8, monkeys: 8 },
                  ]);
                  setStationPosition(0.5);
                }}
                disabled={gameState.isRunning}
                className="ml-2 px-2 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600 disabled:opacity-50"
              >
                原题配置(8家庭)
              </button>
              <button
                onClick={() => {
                  setFamilies([
                    { id: 1, monkeys: 3 },
                    { id: 2, monkeys: 1 },
                    { id: 3, monkeys: 4 },
                  ]);
                  setStationPosition(0.5);
                }}
                disabled={gameState.isRunning}
                className="ml-2 px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 disabled:opacity-50"
              >
                简单示例
              </button>
            </div>
          </div>

          {/* 右侧：基站控制 */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">基站控制</h4>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">基站位置:</span>
                  <span className="bg-purple-100 px-3 py-2 rounded font-bold text-lg">
                    {formatNumber(
                      stationPosition *
                        (families.length > 1 ? families.length - 1 : 1)
                    )}
                  </span>
                  {isOptimal && displayOptions.showOptimalHint && (
                    <span className="text-green-600 text-sm">✅最优</span>
                  )}
                </div>

                {/* 滑动条 */}
                <div className="w-full">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={stationPosition}
                    onChange={handleSliderChange}
                    disabled={gameState.isRunning}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50 slider"
                    style={{
                      background: `linear-gradient(to right, #10b981 0%, #10b981 ${
                        stationPosition * 100
                      }%, #e5e7eb ${stationPosition * 100}%, #e5e7eb 100%)`,
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0</span>
                    <span className="text-center">基站位置滑动条</span>
                    <span>{families.length > 1 ? families.length - 1 : 1}</span>
                  </div>

                  {/* 家庭位置标记 */}
                  <div className="relative mt-1">
                    {calculateFamilyPositions().map((family) => (
                      <div
                        key={`mark-${family.id}`}
                        className="absolute w-0.5 h-2 bg-red-500"
                        style={{
                          left: `${family.position * 100}%`,
                          transform: "translateX(-50%)",
                        }}
                        title={`家庭${family.id}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={startAnimation}
                  disabled={gameState.isRunning}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 flex items-center"
                >
                  <Play size={16} className="mr-2" />
                  开始移动
                </button>
                <button
                  onClick={reset}
                  disabled={gameState.isRunning}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 flex items-center"
                >
                  <RotateCcw size={16} className="mr-2" />
                  重置
                </button>
              </div>

              {displayOptions.showOptimalHint && (
                <div className="text-xs text-gray-500 p-2 bg-gray-50 rounded">
                  理论最优位置:{" "}
                  {formatNumber(
                    optimalPosition *
                      (families.length > 1 ? families.length - 1 : 1)
                  )}
                  <br />
                  💡 使用滑动条精确调整基站位置，会自动吸附到家庭位置
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 主要可视化区域 */}
      <div className="bg-white rounded-lg p-4 mb-4 shadow-md">
        <h3 className="text-lg font-semibold mb-2 text-gray-700">选址地图</h3>
        <svg
          ref={svgRef}
          width="100%"
          height="140"
          viewBox={`0 0 ${calculateMapWidth() + 100} 140`}
          style={{ userSelect: "none" }}
        >
          {(() => {
            const mapWidth = calculateMapWidth();
            const familyPositions = calculateFamilyPositions();

            return (
              <>
                {/* 猴子家庭位置（上方） */}
                {familyPositions.map((family, familyIndex) => {
                  const x = 50 + family.position * mapWidth;
                  const monkeysPerRow = Math.min(6, family.monkeys); // 每行最多6只猴子
                  const rows = Math.ceil(family.monkeys / monkeysPerRow);

                  return (
                    <g key={family.id}>
                      <g transform={`translate(${x}, 35)`}>
                        <text
                          x="0"
                          y="-25"
                          textAnchor="middle"
                          fontSize="11"
                          fill="#dc2626"
                          fontWeight="bold"
                        >
                          🏠家庭{family.id}
                        </text>
                        <text
                          x="0"
                          y="-12"
                          textAnchor="middle"
                          fontSize="9"
                          fill="#dc2626"
                        >
                          {family.monkeys}只
                        </text>

                        {/* 猴子图标 - 多行排列 */}
                        {Array.from({ length: family.monkeys }).map((_, i) => {
                          const row = Math.floor(i / monkeysPerRow);
                          const col = i % monkeysPerRow;
                          const rowMonkeys = Math.min(
                            monkeysPerRow,
                            family.monkeys - row * monkeysPerRow
                          );
                          const startX = -(rowMonkeys - 1) * 5; // 每只猴子间距10px

                          // 计算动画中的位置
                          const baseX = startX + col * 10;
                          const baseY = row * 12; // 行间距12px
                          const targetX = 50 + stationPosition * mapWidth - x;
                          const currentX = gameState.isRunning
                            ? baseX +
                              (targetX - baseX) *
                                (gameState.animationProgress / 100)
                            : baseX;

                          return (
                            <text
                              key={i}
                              x={currentX}
                              y={baseY}
                              textAnchor="middle"
                              fontSize="10"
                              style={{
                                transition: gameState.isRunning
                                  ? "none"
                                  : "all 0.3s ease",
                                transform: gameState.isRunning
                                  ? `translateY(${
                                      Math.sin(
                                        gameState.animationProgress * 0.1 + i
                                      ) * 2
                                    }px)`
                                  : "none",
                              }}
                            >
                              🐵
                            </text>
                          );
                        })}
                      </g>
                    </g>
                  );
                })}

                {/* 背景路径 */}
                <rect
                  x="50"
                  y="65"
                  width={mapWidth}
                  height="20"
                  fill="#e5e7eb"
                  rx="10"
                />

                {/* 家庭位置标记（只保留竖线） */}
                {familyPositions.map((family, index) => {
                  const x = 50 + family.position * mapWidth;
                  return (
                    <g key={`marker-${family.id}`}>
                      <line
                        x1={x}
                        y1="60"
                        x2={x}
                        y2="90"
                        stroke="#dc2626"
                        strokeWidth="2"
                      />
                    </g>
                  );
                })}

                {/* 基站 */}
                <g
                  transform={`translate(${
                    50 + stationPosition * mapWidth
                  }, 75)`}
                >
                  {/* 基站图标：房子+香蕉 */}
                  <circle
                    cx="0"
                    cy="0"
                    r="18"
                    fill="#fbbf24"
                    stroke="#f59e0b"
                    strokeWidth="3"
                  />
                  <text x="-3" y="-3" textAnchor="middle" fontSize="14">
                    🏠
                  </text>
                  <text x="8" y="8" textAnchor="middle" fontSize="12">
                    🍌
                  </text>

                  {/* 基站文字（下方） */}
                  <text
                    x="0"
                    y="35"
                    textAnchor="middle"
                    fontSize="12"
                    fill="#059669"
                    fontWeight="bold"
                  >
                    香蕉基站
                  </text>
                </g>
              </>
            );
          })()}
        </svg>
      </div>

      {/* 轨迹图和统计 */}
      {gameState.isComplete && (
        <div className="space-y-4">
          {/* 轨迹图 */}
          <div className="bg-white rounded-lg p-4 shadow-md">
            <h3 className="text-lg font-semibold mb-2 text-gray-700">
              猴子移动轨迹 (每条箭头代表一只猴子)
            </h3>
            {renderTrajectory()}
          </div>

          {/* 距离统计 */}
          <div className="bg-white rounded-lg p-4 shadow-md">
            <h3 className="font-semibold text-gray-800 mb-3">📊 距离统计</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">总距离:</span>
                <span className="font-bold text-xl text-purple-600">
                  {formatNumber(totalDistance)}
                </span>
              </div>
              {displayOptions.showFamilyContribution &&
                contributions.map((contrib) => (
                  <div
                    key={contrib.familyId}
                    className="flex justify-between text-sm"
                  >
                    <span>家庭{contrib.familyId}:</span>
                    <span>
                      {formatNumber(contrib.distance)}×{contrib.monkeys}=
                      {formatNumber(contrib.contribution)}
                    </span>
                  </div>
                ))}
              {displayOptions.showOptimalHint && (
                <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
                  理论最优位置:{" "}
                  {formatNumber(
                    optimalPosition *
                      (families.length > 1 ? families.length - 1 : 1)
                  )}
                  {Math.abs(stationPosition - optimalPosition) <= 0.05 && (
                    <span className="text-green-600 ml-2">
                      ✅ 当前位置很接近最优！
                    </span>
                  )}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
                💡 距离单位：相邻家庭间距离 = 1
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 历史记录 */}
      {history.length > 0 && (
        <div className="bg-white rounded-lg p-4 shadow-md mt-4">
          <h3 className="font-semibold text-gray-800 mb-3">📈 历史尝试记录</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">基站位置</th>
                  <th className="text-left p-2">总距离</th>
                  <th className="text-left p-2">是否最优</th>
                  <th className="text-left p-2">各家庭贡献</th>
                </tr>
              </thead>
              <tbody>
                {history
                  .slice(-8)
                  .reverse()
                  .map((record) => (
                    <tr key={record.id} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-bold">
                        {formatNumber(
                          record.stationPosition *
                            (families.length > 1 ? families.length - 1 : 1)
                        )}
                      </td>
                      <td className="p-2 font-bold text-purple-600">
                        {formatNumber(record.totalDistance)}
                      </td>
                      <td className="p-2">
                        {Math.abs(record.stationPosition - optimalPosition) <=
                        0.05 ? (
                          <span className="text-green-600">✅ 最优</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-2 text-xs">
                        {record.contributions
                          .map(
                            (c) =>
                              `F${c.familyId}:${formatNumber(c.contribution)}`
                          )
                          .join(", ")}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 显示设置 */}
      <div className="bg-white rounded-lg p-4 shadow-md mt-4">
        <h4 className="font-semibold text-gray-800 mb-3">🎨 显示设置</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showDistanceLabels}
              onChange={(e) =>
                setDisplayOptions((prev) => ({
                  ...prev,
                  showDistanceLabels: e.target.checked,
                }))
              }
              className="rounded"
            />
            <span className="text-sm">距离标签</span>
          </label>

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showOptimalHint}
              onChange={(e) =>
                setDisplayOptions((prev) => ({
                  ...prev,
                  showOptimalHint: e.target.checked,
                }))
              }
              className="rounded"
            />
            <span className="text-sm">最优提示</span>
          </label>

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showFamilyContribution}
              onChange={(e) =>
                setDisplayOptions((prev) => ({
                  ...prev,
                  showFamilyContribution: e.target.checked,
                }))
              }
              className="rounded"
            />
            <span className="text-sm">家庭贡献</span>
          </label>
        </div>
      </div>

      {/* 游戏说明 */}
      <div className="mt-4 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
        <h4 className="font-semibold text-purple-800 mb-2">🎯 探索目标</h4>
        <div className="text-sm text-purple-700 space-y-1">
          <p>
            • 配置猴子家庭、设置基站位置，点击开始移动，可观察到猴子们的移动距离
          </p>
          <p>• 距离单位：相邻家庭间距离 = 1，所有距离都以此为基准</p>
          <p>• 每只猴子都需要走到基站，距离×猴子数量=该家庭的总距离贡献</p>
          <p>• 目标是找到使所有猴子总移动距离最小的基站位置</p>
        </div>
      </div>

      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default StationLocationExplorer;
